import {useEffect, useRef, useState} from "react";
import './CompletionExamRoom.scss';
import Layout from "../Layout/Layout";
import {useCommonStore} from "../../hooks/useCommonStore";
import api from "../../utils/api";
import {useNavigate, useParams} from "react-router-dom";
import cn from "classnames";
import {FormattedMessage, useIntl} from "react-intl";
import Dropdown from "../Dropdown/Dropdown";

const CompletionExamRoom = () => {
  const {id} = useParams();
  const {startLoading, stopLoading} = useCommonStore();
  const intl = useIntl();
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  const [quizIdx, setQuizIdx] = useState(0);
  const [quizArr, setQuizArr] = useState([]);
  const [answerArr, setAnswerArr] = useState([]);
  const [quizType, setQuizType] = useState({});
  const [subQuizCount, setSubQuizCount] = useState({});
  const [currentQuiz, setCurrentQuiz] = useState({});
  const [remainTime, setRemainTime] = useState('00 : 00 : 00');
  const [isLastQuiz, setIsLastQuiz] = useState(false);
  const [quizOptions, setQuizOptions] = useState([]);
  const [shortAnswerFields, setShortAnswerFields] = useState([]); // New state for pre-calculated fields

  useEffect(() => {
    const startExam = async() => {
      startLoading();
      const url = `/v1/user-exam/scheduled/${id}`;

      const [examResponse, userResponse] = await Promise.all([
        api.get(url),
        api.get(url + '/user-data'),
      ]);

      // 시험 정보
      const {examArchived, duration} = examResponse;
      const {examDto: {quizzes}} = examArchived[0];
      const totalQuizCount = quizzes.length;

      // 유저 답안
      const {startAt, userAnswer} = userResponse;

      if (userAnswer) {
        setAnswerArr(userAnswer);

        for (let i = 0; i < totalQuizCount; i++) {
          if (!userAnswer[i] || userAnswer[i].length === 0) {
            setQuizIdx(i);
            break;
          }
        }
      }

      const dueDt = new Date(startAt);
      dueDt.setMinutes(dueDt.getMinutes() + Number(duration));

      const updateRemainingTime = async() => {
        const nowDt = new Date();
        const timeDifference = dueDt - nowDt;

        if (timeDifference < 0) {
          // 시간 초과
          alert(intl.formatMessage({id: '@exam_room_time_ended'}));
          window.location.href = '/main';
        } else {
          const hours = Math.floor(timeDifference / (1000 * 60 * 60));
          const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

          const formattedTime = `${String(hours).padStart(2, '0')} : ${String(minutes).padStart(2, '0')} : ${String(seconds).padStart(2, '0')}`;
          setRemainTime(formattedTime);
        }
      }

      updateRemainingTime();
      intervalRef.current = setInterval(updateRemainingTime, 1000);

      setQuizArr(quizzes);
      stopLoading();
    }
    startExam();

    return () => {
      clearInterval(intervalRef.current);
    }
  }, []);

  useEffect(() => {
    setQuizType(quizArr[quizIdx]?.quizType);
    const currentQuizContext = quizArr[quizIdx]?.quizI18n[0]?.context;
    let calculatedSubQuizCount = 0;
    const fieldsToRender = []; // To store the pre-calculated info

    if (quizArr[quizIdx]?.quizType === 'SHORT_ANSWER' && currentQuizContext?.responseFields?.length > 0) {
      let globalIndex = 0;
      for (let fieldIdx = 0; fieldIdx < currentQuizContext.responseFields.length; fieldIdx++) {
        const fieldLabelGroup = currentQuizContext.responseFields[fieldIdx];
        const subLabels = fieldLabelGroup.split(',');

        for (let subFieldIdx = 0; subFieldIdx < subLabels.length; subFieldIdx++) {
          fieldsToRender.push({
            displayNo: subFieldIdx === 0 ? fieldIdx + 1 : '',
            label: subLabels[subFieldIdx].trim(),
            globalIndex: globalIndex, // This is `i` in the rendering loop
          });
          globalIndex++;
        }
      }
      calculatedSubQuizCount = globalIndex; // Total count of individual inputs
    } else {
      calculatedSubQuizCount = quizArr[quizIdx]?.subQuizCount ?? 0;
      // If no responseFields, or not SHORT_ANSWER, we still need to populate fieldsToRender for generic case
      for (let i = 0; i < calculatedSubQuizCount; i++) {
        fieldsToRender.push({
          displayNo: i + 1,
          label: '', // No specific label
          globalIndex: i,
        });
      }
    }
    setSubQuizCount(calculatedSubQuizCount);
    setShortAnswerFields(fieldsToRender); // Set the pre-calculated fields
    setCurrentQuiz(quizArr[quizIdx]?.quizI18n[0]);
    setIsLastQuiz(quizIdx === quizArr.length - 1);
  }, [quizArr, quizIdx]);

  useEffect(() => {
    if (quizArr.length > 0) {
      setQuizOptions(quizArr.map((_, i) => ({name: `${i + 1}`, value: i.toString()})));
    }
  }, [quizArr]);

  const handleSelectChoice = (choiceNo) => {
    const choiceStr = choiceNo.toString();
    const nextArr = [...answerArr];

    if (!nextArr[quizIdx]) {
      nextArr[quizIdx] = [];
    }

    if (!nextArr[quizIdx].includes(choiceStr)) {
      nextArr[quizIdx].push(choiceStr);
    } else {
      nextArr[quizIdx] = nextArr[quizIdx].filter(element => element !== choiceStr);
    }

    setAnswerArr(nextArr);
  };

  const handlePrevQuiz = () => {
    saveUserAnswer(false);
    if (quizIdx !== 0) {
      setQuizIdx(quizIdx - 1);
    }
  }

  const handleNextQuiz = () => {
    if (!validateAnswer()) {
      return;
    }
    saveUserAnswer(false);
    if (quizIdx < quizArr.length) {
      setQuizIdx(quizIdx + 1);
    }
  }

  const handleSelectQuiz = (value) => {
    if (!validateAnswer()) {
      return; // 유효성 검사 실패 시 quizIdx를 변경하지 않고 함수 종료
    }
    saveUserAnswer(false);
    setQuizIdx(Number(value));
  }

  const validateAnswer = () => {
    if (!answerArr[quizIdx] || answerArr[quizIdx].length === 0) {
      if (quizType === 'SHORT_ANSWER') {
        if (subQuizCount > 1) {
          alert(intl.formatMessage({id: '@exam_room_alert_fill_all_answers'}));
        } else {
          alert(intl.formatMessage({id: '@exam_room_alert_no_short_answer'}));
        }
      } else { // CHOICES
        alert(intl.formatMessage({id: '@exam_room_alert_no_answer'}));
      }
      return false;
    }

    if (quizType === 'SHORT_ANSWER') {
      for (let i = 0; i < subQuizCount; i++) {
        if (!answerArr[quizIdx][i] || answerArr[quizIdx][i].trim() === '') {
          alert(intl.formatMessage({id: '@exam_room_alert_fill_all_answers'}));
          return false;
        }
      }
    }
    return true;
  }

  const handleSubmitQuiz = async() => {
    if (!validateAnswer()) {
      return;
    }

    if (window.confirm(intl.formatMessage({id: '@exam_room_submit_confirm'}))) {
      const data = await saveUserAnswer(true);
      if (data) {
        alert(intl.formatMessage({id: '@exam_room_submit_success'}));
        navigate('/main');
      }
    }
  }

  const saveUserAnswer = async(isSubmit) => {
    let userAnswerToSave = [];
    for (let i = 0; i < quizArr.length; i++) {
      const quiz = quizArr[i];
      const currentAnswerForQuiz = answerArr[i] || [];

      if (quiz.quizType === 'SHORT_ANSWER') {
        const contextResponseFields = quiz.quizI18n[0].context?.responseFields;
        if (contextResponseFields?.length > 0) {
          let globalAnswerIndex = 0;
          const groupedAnswers = [];

          for (const fieldLabelGroup of contextResponseFields) {
            const subLabels = fieldLabelGroup.split(',');
            const answersForGroup = [];

            for (let j = 0; j < subLabels.length; j++) {
              // Use globalAnswerIndex to get the correct answer from the flat array
              answersForGroup.push(currentAnswerForQuiz[globalAnswerIndex] || '');
              globalAnswerIndex++;
            }
            groupedAnswers.push(answersForGroup.join('|'));
          }
          userAnswerToSave.push(groupedAnswers); // Push the array of grouped strings
        } else {
          // If no responseFields, assume it's the old style short answer (single answer per subQuizCount)
          userAnswerToSave.push(currentAnswerForQuiz);
        }
      } else { // CHOICES
        userAnswerToSave.push(currentAnswerForQuiz);
      }
    }
    return await api.put(`/v1/user-exam/scheduled/${id}/user-data`, {userAnswer: userAnswerToSave, isSubmit});
  }
  
  const handleShortAnswerChange = (e, subQuizNo = 0) => {
    const nextArr = [...answerArr];
    if (!nextArr[quizIdx]) {
      nextArr[quizIdx] = [];
    }
    nextArr[quizIdx][subQuizNo] = e.target.value;
    setAnswerArr(nextArr);
  };

  return (
    <Layout>
      <div className='CompletionExamRoom'>
        <section className='progress'>
          <div className='progress-item'>
            <span className='label'>
              <FormattedMessage id='@exam_room_time_remaining' />
            </span>
            <span className='time'>{remainTime}</span>
          </div>
          <div className='progress-item'>
            <span className='label'>
                <FormattedMessage id='@exam_room_progress' />
            </span>
            <span className='count'>
              <Dropdown
                  width={80}
                  options={quizOptions}
                  defaultValue={quizIdx.toString()}
                  handleSelect={handleSelectQuiz}
              />/{quizArr.length}
            </span>
            <div className='bars'>
              <div className='empty-bar' />
              <div
                className='filled-bar'
                style={{width: ((quizIdx + 1) / quizArr.length * 100).toString() + '%'}}
              />
            </div>
          </div>
        </section>

        <section className='quiz'>
          <div className='title'>
            <span className='quiz-no'>{quizIdx + 1}.</span>
            <span dangerouslySetInnerHTML={{ __html: currentQuiz?.title?.replace(/{([^}]+)}/g, '<u>$1</u>') }} />
          </div>
          {quizType=='CHOICES' &&
            currentQuiz?.context?.choices.map((choice, i) => {
              const isChecked = answerArr[quizIdx]?.includes((i+1).toString());
              return (
                <div
                  key={i}
                  className={cn('choice', {'is-checked': isChecked})}
                  onClick={() => handleSelectChoice(i+1)}
                >
                  {isChecked && <div className='checkmark'>&#10003;</div>}
                  <div className='no'>{i+1}</div> {choice}
                </div>
              );
            })
          }
          {quizType === 'SHORT_ANSWER' && (
            shortAnswerFields.map((field, i) => { // Use 'field' from pre-calculated array
              const displayLabel = field.label && field.label.trim() !== '';

              return (
                <div key={i} className='short-answer-sub-quiz'>
                  {field.displayNo ? <div className='no'>{field.displayNo})</div> : <div className='no empty'></div>}
                  {displayLabel && (
                    <div className='response-field-label'>{field.label}</div>
                  )}
                  <textarea
                    className='short-answer-textarea'
                    value={answerArr[quizIdx]?.[field.globalIndex] || ''}
                    onChange={(e) => handleShortAnswerChange(e, field.globalIndex)}
                  />
                </div>
              );
            })
          )}
        </section>

        <section className='buttons'>
          {
            quizIdx !== 0 &&
            <button
              className='prev'
              onClick={handlePrevQuiz}
            >
              <FormattedMessage id='@exam_room_prev_page' />
            </button>
          }
          {
            !isLastQuiz ?
              <button
                className='next'
                onClick={handleNextQuiz}
              >
                <FormattedMessage id='@exam_room_next_page' />
              </button>
              :
              <button
                className='next'
                onClick={handleSubmitQuiz}
              >
                <FormattedMessage id='@exam_room_submit_btn' />
              </button>
          }
        </section>
      </div>
    </Layout>
  );
}

export default CompletionExamRoom;